import type { GatewayBrowserClient } from "../gateway.ts";
import type {
  AgentsListResult,
  BibleFinalSummary,
  FleetDecisionItem,
  FleetWorkPlanRecord,
  FleetWorkPlanSummary,
} from "../types.ts";

function isUnknownWorkPlanMethodError(err: unknown): boolean {
  return String(err).includes("unknown method: fleet.workPlan");
}

export function formatFleetWorkPlanError(err: unknown): string {
  if (isUnknownWorkPlanMethodError(err)) {
    return "Work plan needs a newer gateway. Run pnpm build, restart the gateway, and refresh.";
  }
  return String(err);
}

export type FleetWorkPlanState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  agentsSelectedId: string | null;
  workPlansLoading: boolean;
  workPlansError: string | null;
  workPlans: FleetWorkPlanSummary[];
  workPlanYoutrackConfigured: boolean;
  workPlanSelectedId: string | null;
  workPlanDetail: FleetWorkPlanRecord | null;
  workPlanDetailLoading: boolean;
  workPlanBusy: boolean;
  workPlanOpenDecisions: FleetDecisionItem[];
  workPlanBibleSummary: BibleFinalSummary | null;
};

export async function loadFleetWorkPlans(state: FleetWorkPlanState) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    state.workPlans = [];
    state.workPlansError = null;
    return;
  }
  state.workPlansLoading = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{
      agentId: string;
      plans: FleetWorkPlanSummary[];
      youtrackConfigured: boolean;
    }>("fleet.workPlan.list", { agentId: state.agentsSelectedId });
    state.workPlans = Array.isArray(res?.plans) ? res.plans : [];
    state.workPlanYoutrackConfigured = Boolean(res?.youtrackConfigured);
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
    state.workPlans = [];
  } finally {
    state.workPlansLoading = false;
  }
}

export async function loadFleetWorkPlanDetail(state: FleetWorkPlanState, planId: string) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  state.workPlanDetailLoading = true;
  try {
    const res = await state.client.request<{
      plan: FleetWorkPlanRecord;
      openDecisions?: FleetDecisionItem[];
      bibleSummary?: BibleFinalSummary;
    }>("fleet.workPlan.get", {
      agentId: state.agentsSelectedId,
      planId,
    });
    state.workPlanDetail = res?.plan ?? null;
    state.workPlanOpenDecisions = Array.isArray(res?.openDecisions) ? res.openDecisions : [];
    state.workPlanBibleSummary = res?.bibleSummary ?? null;
    state.workPlanSelectedId = planId;
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
    state.workPlanDetail = null;
  } finally {
    state.workPlanDetailLoading = false;
  }
}

export async function createFleetWorkPlan(
  state: FleetWorkPlanState,
  params: {
    goal: string;
    plannerAgentId: string;
    youtrackProject?: string;
    parentSessionKey?: string;
  },
): Promise<FleetWorkPlanRecord | null> {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return null;
  }
  const goal = params.goal.trim();
  if (!goal) {
    return null;
  }
  state.workPlanBusy = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{ plan: FleetWorkPlanRecord }>("fleet.workPlan.create", {
      agentId: state.agentsSelectedId,
      plannerAgentId: params.plannerAgentId,
      goal,
      youtrackProject: params.youtrackProject?.trim() || undefined,
      parentSessionKey: params.parentSessionKey?.trim() || undefined,
    });
    const plan = res?.plan ?? null;
    if (plan) {
      state.workPlanDetail = plan;
      state.workPlanSelectedId = plan.id;
      await loadFleetWorkPlans(state);
    }
    return plan;
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
    return null;
  } finally {
    state.workPlanBusy = false;
  }
}

export async function stopFleetWorkPlan(state: FleetWorkPlanState, planId: string) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  state.workPlanBusy = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{
      plan: FleetWorkPlanRecord;
      stoppedSubagents?: number;
    }>("fleet.workPlan.stop", {
      agentId: state.agentsSelectedId,
      planId,
    });
    state.workPlanDetail = res?.plan ?? state.workPlanDetail;
    await loadFleetWorkPlans(state);
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
  } finally {
    state.workPlanBusy = false;
  }
}

export async function resumeFleetWorkPlan(state: FleetWorkPlanState, planId: string) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  state.workPlanBusy = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{ plan: FleetWorkPlanRecord }>("fleet.workPlan.resume", {
      agentId: state.agentsSelectedId,
      planId,
    });
    state.workPlanDetail = res?.plan ?? state.workPlanDetail;
    await loadFleetWorkPlans(state);
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
  } finally {
    state.workPlanBusy = false;
  }
}

export async function approveFleetWorkPlan(state: FleetWorkPlanState, planId: string) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  state.workPlanBusy = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{ plan: FleetWorkPlanRecord }>(
      "fleet.workPlan.approve",
      {
        agentId: state.agentsSelectedId,
        planId,
      },
    );
    state.workPlanDetail = res?.plan ?? state.workPlanDetail;
    await loadFleetWorkPlans(state);
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
  } finally {
    state.workPlanBusy = false;
  }
}

export async function publishFleetWorkPlanYouTrack(
  state: FleetWorkPlanState,
  planId: string,
  project: string,
) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  state.workPlanBusy = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{ plan: FleetWorkPlanRecord }>(
      "fleet.workPlan.publishYouTrack",
      {
        agentId: state.agentsSelectedId,
        planId,
        project: project.trim(),
      },
    );
    state.workPlanDetail = res?.plan ?? state.workPlanDetail;
    await loadFleetWorkPlans(state);
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
  } finally {
    state.workPlanBusy = false;
  }
}

export async function spawnFleetWorkPlanUnit(
  state: FleetWorkPlanState,
  planId: string,
  unitId: string,
  sessionKey: string,
  workerAgentId?: string,
) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  state.workPlanBusy = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{
      plan: FleetWorkPlanRecord | null;
      unitId: string;
      childSessionKey: string;
      runId: string;
      warning?: string;
    }>("fleet.workPlan.spawnUnit", {
      agentId: state.agentsSelectedId,
      planId,
      unitId,
      sessionKey: sessionKey.trim(),
      workerAgentId: workerAgentId?.trim() || undefined,
    });
    state.workPlanDetail = res?.plan ?? state.workPlanDetail;
    await loadFleetWorkPlans(state);
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
  } finally {
    state.workPlanBusy = false;
  }
}

export async function resolveFleetWorkPlanDecision(
  state: FleetWorkPlanState,
  params: { planId: string; decisionId: string; resolution: "continue" | "stop" },
) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  state.workPlanBusy = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{
      plan: FleetWorkPlanRecord;
      openDecisions?: FleetDecisionItem[];
      bibleSummary?: BibleFinalSummary;
    }>("fleet.workPlan.resolveDecision", {
      agentId: state.agentsSelectedId,
      planId: params.planId,
      decisionId: params.decisionId,
      resolution: params.resolution,
    });
    state.workPlanDetail = res?.plan ?? state.workPlanDetail;
    state.workPlanOpenDecisions = Array.isArray(res?.openDecisions) ? res.openDecisions : [];
    state.workPlanBibleSummary = res?.bibleSummary ?? null;
    await loadFleetWorkPlans(state);
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
  } finally {
    state.workPlanBusy = false;
  }
}

export async function undoFleetWorkPlan(state: FleetWorkPlanState, planId: string) {
  if (!state.client || !state.connected || !state.agentsSelectedId) {
    return;
  }
  state.workPlanBusy = true;
  state.workPlansError = null;
  try {
    const res = await state.client.request<{ plan: FleetWorkPlanRecord }>("fleet.workPlan.undo", {
      agentId: state.agentsSelectedId,
      planId,
    });
    state.workPlanDetail = res?.plan ?? state.workPlanDetail;
    await loadFleetWorkPlanDetail(state, planId);
    await loadFleetWorkPlans(state);
  } catch (err) {
    state.workPlansError = formatFleetWorkPlanError(err);
  } finally {
    state.workPlanBusy = false;
  }
}

export function resetWorkPlanSelection(state: FleetWorkPlanState) {
  state.workPlanSelectedId = null;
  state.workPlanDetail = null;
  state.workPlanOpenDecisions = [];
  state.workPlanBibleSummary = null;
}

export type FleetSessionActivityView = {
  sessionKey: string;
  runningUnits: Array<{
    planId: string;
    unitId: string;
    title: string;
    status: string;
    childSessionKey?: string;
    runId?: string;
  }>;
  plannedUnits: Array<{
    planId: string;
    unitId: string;
    title: string;
    status: string;
    childSessionKey?: string;
    runId?: string;
  }>;
};

export async function loadFleetSessionActivity(
  state: {
    client: GatewayBrowserClient | null;
    connected: boolean;
    agentsSelectedId?: string | null;
  },
  params: { sessionKey: string; agentId?: string },
): Promise<FleetSessionActivityView | null> {
  const sessionKey = params.sessionKey.trim();
  const agentId = (params.agentId ?? state.agentsSelectedId ?? "").trim();
  if (!state.client || !state.connected || !sessionKey || !agentId) {
    return null;
  }
  try {
    const res = await state.client.request<FleetSessionActivityView>(
      "fleet.workPlan.sessionActivity",
      { sessionKey, agentId },
    );
    if (!res?.sessionKey) {
      return null;
    }
    return res;
  } catch {
    return null;
  }
}
